import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Form, Input, Row, Space, Spin, Tag, Typography } from 'antd';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { UserOutlined } from '@ant-design/icons';
import tileStyles from 'src/styles/tile.module.css';

import { useForm } from 'antd/es/form/Form';
import { AppDispatch } from 'src/index';
import { useUsers } from 'src/features/users/users.hooks';
import { createUser } from 'src/features/users/users.state';
import { DraftUser, RoleEnum } from 'src/features/users/users.types';

const emptyUser = { email: '', password: '' };

const CmsUsersPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const [{ users }] = useUsers();
  const history = useHistory();

  const [form] = useForm();

  const [createMode, setCreateMode] = useState<boolean>(false);
  const [newEntity, setNewEntity] = useState<Omit<DraftUser, 'role'>>(emptyUser);

  const handleCreateButtonClick = useCallback(() => {
    setCreateMode(true);
  }, []);

  const handleFormSubmit = useCallback(async () => {
    const nextUser = {
      ...newEntity,
      role: RoleEnum.observer,
      name: newEntity.email.split('@')[0],
    };
    const response = await dispatch(createUser(nextUser));

    if (createUser.fulfilled.match(response)) {
      history.push(`/settings/users/${response.payload.id}`);
    }
  }, [dispatch, history, newEntity]);

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/settings/users'>
                <Space>
                  <UserOutlined />
                  Пользователи CMS
                </Space>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            Пользователи CMS
          </Typography.Title>
          <div>
            {createMode && (
              <Form
                form={form}
                validateTrigger='onBlur'
                layout='vertical'
                className={tileStyles.listTile}
                onFinish={handleFormSubmit}
              >
                <UserOutlined />
                <Form.Item name='name' rules={[{ required: true, type: 'email' }]} noStyle>
                  <Input
                    type='text'
                    name='name'
                    placeholder='Введите почту'
                    style={{ width: '35%' }}
                    onChange={ev => setNewEntity(e => ({ ...e, email: ev.target.value }))}
                  />
                </Form.Item>
                <Form.Item name='password' rules={[{ required: true }]} noStyle>
                  <Input
                    type='password'
                    name='password'
                    placeholder='Введите пароль'
                    style={{ width: '35%' }}
                    onChange={ev => setNewEntity(e => ({ ...e, password: ev.target.value }))}
                  />
                </Form.Item>
                <Button htmlType='button' type='default' onClick={() => setCreateMode(false)}>
                  Отменить
                </Button>
                <Button htmlType='submit' type='primary' disabled={newEntity.email === '' || newEntity.password === ''}>
                  Создать
                </Button>
              </Form>
            )}
            {users.map(c => (
              <div key={c.id} className={tileStyles.listTile}>
                <Link to={`/settings/users/${c.id}`} className={tileStyles.tileName}>
                  <Space>
                    <UserOutlined />
                    <span>
                      {c.username} ({c.email})
                    </span>
                  </Space>
                </Link>
                <Space align='center'>
                  {c.blocked && <Tag color='red'>Заблокирован</Tag>}
                  {c.admin && <Tag color='green'>Администратор</Tag>}
                  {c.role && <Tag>{c.role}</Tag>}
                </Space>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.sidebar}>
          <Space>
            <Button type='primary' onClick={handleCreateButtonClick}>
              Создать нового пользователя
            </Button>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const CmsUsersPageBlock: React.FC = () => {
  const [{ isPending }, fetchUsers] = useUsers();

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  if (isPending) {
    return (
      <MainLayout>
        <Row align={'middle'} justify={'center'} style={{ minHeight: '100vh' }}>
          <Spin size={'large'} />
        </Row>
      </MainLayout>
    );
  }

  return <CmsUsersPage />;
};

export default CmsUsersPageBlock;
